import React, { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridExpandMoreIcon,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import ProvideCreditAmountStatus from '~/components/Vouchers/ProvideCreditAmountStatus';
import Table from '~/components/v2/common/Table/Table';

import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';

import { ListVoucherLogsQuery, PromoRequestFilters, VoucherLog } from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';
import { getVoucherLogDisplayMode, isVoucherLogProvideCredit } from '~/utils/voucherLogDisplayMode';

import ColumnSelector from '../PromoTester/ColumnSelector';
import PromoCodeUser from '../formatters/PromoCodeUser';

interface PromoRequestVoucherLogsProps {
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  voucherNames?: string[];
  userId?: string;
  brand?: string;
  promoRequestsFilter?: PromoRequestFilters;
  setPromoRequestsFilter?: Dispatch<SetStateAction<PromoRequestFilters>>;
  updateUrlState?: (filter: {
    userId?: string;
    voucherName?: string;
    expanded?: boolean;
    itemsExpanded?: boolean;
  }) => void;
  setFormUserId?: (userId: string) => void;
  setFormVoucherName?: (voucherName: string) => void;
}

const sharedAttrs: Pick<GridColDef, 'sortable' | 'display' | 'flex'> = {
  sortable: false,
  display: 'flex',
  flex: 1,
};

const DEFAULT_PAGE_SIZE = 20;

function PromoRequestVoucherLogs({
  expanded,
  onExpandedChange,
  voucherNames,
  userId,
  brand,
  setPromoRequestsFilter,
  updateUrlState,
  setFormUserId,
  setFormVoucherName,
}: PromoRequestVoucherLogsProps) {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    voucherLogId: false,
    voucherUserId: false,
    voucherDescription: false,
    voucherToggles: false,
    deletedAt: false,
    createdBy: false,
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: visible,
    }));
  };

  const handleFilterByVoucherName = useCallback(
    (voucherName: string) => {
      updateUrlState?.({ voucherName });
      setPromoRequestsFilter?.((prev) => ({ ...prev, voucherName }));
      setFormVoucherName?.(voucherName);
    },
    [updateUrlState, setPromoRequestsFilter, setFormVoucherName],
  );

  const handleFilterByUserId = useCallback(
    (nextUserId: string) => {
      updateUrlState?.({ userId: nextUserId, expanded: true, itemsExpanded: true });
      setPromoRequestsFilter?.((prev) => ({ ...prev, userId: nextUserId }));
      setFormUserId?.(nextUserId);
    },
    [updateUrlState, setPromoRequestsFilter, setFormUserId],
  );

  const query = useMemo<ListVoucherLogsQuery>(() => {
    const q: ListVoucherLogsQuery = {
      page: paginationModel.page,
      limit: paginationModel.pageSize,
      sortBy: 'created_at',
      sortByDirection: 'desc',
    };
    if (voucherNames?.length) {
      q.voucherNames = voucherNames;
    }
    if (userId) {
      q.userId = userId;
    }
    if (brand === 'luxuryescapes') {
      q.brand = 'luxuryescapes';
    }
    return q;
  }, [paginationModel, voucherNames, userId, brand]);

  const { data, isLoading } = useListVoucherLogs({ query });

  const rows = data?.result?.logs ?? [];
  const total = Number(data?.result?.total ?? 0);
  const displayMode = useMemo(() => getVoucherLogDisplayMode(rows), [rows]);

  const columns: Array<GridColDef<VoucherLog>> = useMemo(() => {
    const dash = (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );

    const discountAmountCell = (params: GridRenderCellParams<VoucherLog>) => {
      const currency = params.row.metadata?.voucherCurrency || params.row.currencyCode;
      const remaining = params.row.metadata?.remainingAmountAfter;
      return (
        <Typography component="span" color="success.main">
          {currencyFormatter(currency, params.row.amount)}
          {remaining != null && remaining > 0 ? ` (${currencyFormatter(currency, remaining)} left)` : ''}
        </Typography>
      );
    };

    const creditAmountCell = (params: GridRenderCellParams<VoucherLog>) => {
      const currency = params.row.metadata?.voucherCurrency || params.row.currencyCode;
      return (
        <Typography
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}
        >
          <Typography component="span" color="success.main" sx={{ whiteSpace: 'nowrap' }}>
            {currencyFormatter(currency, params.row.amount)} Credit
          </Typography>
          <ProvideCreditAmountStatus row={params.row} />
        </Typography>
      );
    };

    const amountColumns: Array<GridColDef<VoucherLog>> =
      displayMode === 'discount-only'
        ? [
            {
              field: 'amount',
              headerName: 'Discount amount',
              ...sharedAttrs,
              renderCell: (params) => discountAmountCell(params),
            },
          ]
        : displayMode === 'provide-credit-only'
        ? [
            {
              field: 'amount',
              headerName: 'Credit payout',
              ...sharedAttrs,
              renderCell: (params) => creditAmountCell(params),
            },
          ]
        : [
            {
              field: 'amount',
              headerName: 'Discount amount',
              ...sharedAttrs,
              renderCell: (params) => (isVoucherLogProvideCredit(params.row) ? dash : discountAmountCell(params)),
            },
            {
              field: 'creditPayoutAmountDisplay',
              headerName: 'Credit payout',
              ...sharedAttrs,
              renderCell: (params) => (isVoucherLogProvideCredit(params.row) ? creditAmountCell(params) : dash),
            },
          ];

    const creditTimelineColumns: Array<GridColDef<VoucherLog>> =
      displayMode === 'discount-only'
        ? []
        : [
            {
              field: 'creditGrantedAt',
              headerName: 'Credit granted',
              ...sharedAttrs,
              valueGetter: (_value, row) => row.metadata?.creditGrantedAt,
              renderCell: (params) =>
                params.row.metadata?.creditGrantedAt ? (
                  <DateFormatterDetailed date={params.row.metadata.creditGrantedAt} />
                ) : (
                  dash
                ),
            },
            {
              field: 'creditPayoutAt',
              headerName: 'Credit payout at',
              ...sharedAttrs,
              valueGetter: (_value, row) => row.metadata?.creditPayoutAt,
              renderCell: (params) => {
                const payoutAt = params.row.metadata?.creditPayoutAt;
                if (!payoutAt) {
                  return dash;
                }
                const highlightIfPast = !params.row.metadata?.creditGrantedAt && !(new Date(payoutAt) > new Date());
                return <DateFormatterDetailed date={payoutAt} highlightIfPast={highlightIfPast} />;
              },
            },
          ];

    const remainingAfterColumns: Array<GridColDef<VoucherLog>> =
      displayMode === 'provide-credit-only'
        ? []
        : [
            {
              field: 'remainingAmountAfter',
              headerName: 'Remaining after discount',
              ...sharedAttrs,
              valueGetter: (_value, row) => row.metadata?.remainingAmountAfter,
              renderCell: (params) => {
                if (isVoucherLogProvideCredit(params.row)) {
                  return dash;
                }
                const currency = params.row.metadata?.voucherCurrency || params.row.currencyCode;
                return (
                  <Typography>{currencyFormatter(currency, params.row.metadata?.remainingAmountAfter)}</Typography>
                );
              },
            },
          ];

    return [
      {
        field: 'createdAt',
        headerName: 'Created At',
        ...sharedAttrs,
        renderCell: (params) => <DateFormatterDetailed date={params.row.createdAt} />,
      },
      {
        field: 'voucherName',
        headerName: 'Voucher Name',
        ...sharedAttrs,
        flex: 1,
        valueGetter: (_value, row) => row.metadata?.voucherName ?? row.metadata?.voucherTitle ?? '',
        renderCell: (params) => {
          const name = params.row.metadata?.voucherName || params.row.metadata?.voucherTitle;
          if (!name) return null;
          return (
            <Stack sx={{ width: '100%', minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => handleFilterByVoucherName(name)}
                title="Click to filter by this voucher name"
              >
                {name}
              </Typography>
              <CopyableField
                value={params.row.voucherId}
                component="div"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  '& > .MuiIconButton-root': { flexShrink: 0 },
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Box
                      title={params.row.voucherId}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {params.row.voucherId}
                    </Box>
                    <Tooltip title="Open voucher">
                      <IconButton
                        component={Link}
                        href={`/marketing/vouchers/${params.row.voucherId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
            </Stack>
          );
        },
      },
      {
        field: 'userId',
        headerName: 'User',
        ...sharedAttrs,
        renderCell: (params) =>
          params.row.userId ? (
            <PromoCodeUser
              userId={params.row.userId}
              userIdFor={params.row.userId}
              onUserIconClick={() => handleFilterByUserId(params.row.userId as string)}
            />
          ) : null,
      },
      {
        field: 'fkOrder',
        headerName: 'Order ID',
        ...sharedAttrs,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
              {params.row.fkOrder}
            </Box>
            <Tooltip title="Open order">
              <IconButton
                component={Link}
                href={`/purchases/${params.row.fkOrder}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      ...amountColumns,
      ...creditTimelineColumns,
      ...remainingAfterColumns,
      {
        field: 'currencyCode',
        headerName: 'Currency',
        ...sharedAttrs,
        flex: 0,
      },
      {
        field: 'expiresAt',
        headerName: 'Expires At',
        ...sharedAttrs,
        valueGetter: (_value, row) => row.metadata?.expiredAt,
        renderCell: (params) =>
          params.row.metadata?.expiredAt ? <DateFormatterDetailed date={params.row.metadata.expiredAt} /> : null,
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        ...sharedAttrs,
        renderCell: (params) => <PromoCodeUser userId={params.row.createdBy} userIdFor={params.row.createdBy} />,
      },
      {
        field: 'deletedAt',
        headerName: 'Deleted At',
        ...sharedAttrs,
        renderCell: (params) =>
          params.row.deletedAt ? (
            <DateFormatterDetailed date={params.row.deletedAt} />
          ) : (
            <Typography variant="body2">Active</Typography>
          ),
      },
      {
        field: 'voucherDescription',
        headerName: 'Description',
        ...sharedAttrs,
        valueGetter: (_value, row) => row.metadata?.voucherDescription,
      },
      {
        field: 'voucherToggles',
        headerName: 'Toggles',
        ...sharedAttrs,
        valueGetter: (_value, row) => row.metadata?.voucherToggles,
        renderCell: (params) => (
          <Typography variant="body2">{params.row.metadata?.voucherToggles?.join(', ')}</Typography>
        ),
      },
      {
        field: 'debug',
        headerName: '',
        ...sharedAttrs,
        flex: 0,
        renderCell: (params) => (
          <DebugModal
            type="generic"
            data={params.row}
            title={`Voucher ${params.row.metadata?.voucherName ?? params.row.voucherId}`}
          />
        ),
      },
      {
        field: 'promoTester',
        headerName: 'Promo tester',
        ...sharedAttrs,
        flex: 0,
        minWidth: 110,
        renderCell: (params) => {
          const orderId = params.row.fkOrder;
          const voucherId = params.row.voucherId;
          if (!orderId || !voucherId) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const search = new URLSearchParams({
            orderId: String(orderId),
            voucherId: String(voucherId),
          });
          if (brand) {
            search.set('brand', brand);
          }
          return (
            <Tooltip title="Open promo tester with this order ID and voucher ID">
              <IconButton
                component={Link}
                href={`/marketing/promo-tester?${search.toString()}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'voucherLogId',
        headerName: 'Log ID',
        ...sharedAttrs,
      },
      {
        field: 'voucherUserId',
        headerName: 'Voucher User ID',
        ...sharedAttrs,
      },
    ];
  }, [
    displayMode,
    brand,
    updateUrlState,
    setFormUserId,
    setFormVoucherName,
    handleFilterByVoucherName,
    handleFilterByUserId,
  ]);

  if (isLoading && !rows.length) {
    return <Spinner size={36} />;
  }

  return (
    <Accordion expanded={expanded} onChange={(_, expanded) => onExpandedChange?.(expanded)}>
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon />}
        aria-controls="voucher-logs"
        id="voucher-logs"
        sx={{
          backgroundColor: 'action.hover',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <PageSubheader title={`Voucher Logs (${total})`} />
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 1 }}>
          <ColumnSelector
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityChange={handleColumnVisibilityChange}
          />
        </Stack>

        <Table
          columns={columns}
          rows={rows}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          totalRowCount={total}
          isLoading={isLoading}
          getRowId={(row: VoucherLog) => row.voucherLogId}
          dataGridProps={{
            getRowHeight: () => 'auto',
            columnVisibilityModel,
            onColumnVisibilityModelChange: setColumnVisibilityModel,
            pageSizeOptions: [10, 20, 50, 100],
            slots: { pagination: GridPagination },
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default PromoRequestVoucherLogs;
